import * as React from 'react'
import styled from 'styled-components'
import { HelpCenterLink, PublicLocale } from '../../types'
import { useTranslation } from 'react-i18next'
import Button from '../Button/Button'
import { DragDropContext, Draggable, Droppable, DropResult, ResponderProvided } from '@hello-pangea/dnd'
import SimpleTooltip from '@/components/Tooltips/SimpleTooltip'

const Container = styled.div`
  display: flex; 
  flex-direction: column;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  padding-bottom: 4px;
`

const HeaderLabel = styled.div`
  flex-grow: 1;
  font-weight: bold;
  font-size: 14px;
  color: #646462;
`

const Links = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`

const Link = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  font-weight: normal !important;

  input {
    font-weight: normal !important;
  }
`

const LinkHandle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const LinkDelete = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  cursor: pointer;

  background: #f8f8f7;
  color: #81817e;
  fill: #81817e;

  svg {
    width: 16px;
    height: 16px;
  }
`

interface IProps {
  locale: PublicLocale
  links: HelpCenterLink[]
  onChange: (links: HelpCenterLink[]) => void
}

const LinksBuilder = (props: IProps) => {
  const { t } = useTranslation()
  const { links, locale } = props
  const activeLinks = links.filter(link => link.locale === locale)

  const onAddLinkClick = () => {
    if (activeLinks.length <= 2) {
      const newLink: HelpCenterLink = {
        locale: locale,
        label: '',
        url: ''
      }
      props.onChange([...links, newLink])
    }
  }

  const onDragEnd = (dropResult: DropResult, provided: ResponderProvided) => {
    const { destination, source } = dropResult

    // If there's no destination (dropped outside), do nothing
    if (!destination) {
      return
    }

    // If the item was dropped in the same position, do nothing
    if (destination.index === source.index) {
      return
    }

    // Create a new array with reordered items
    const reorderedLinks = Array.from(links)
    const [movedLink] = reorderedLinks.splice(source.index, 1)
    reorderedLinks.splice(destination.index, 0, movedLink)

    // Update the parent component with the new order
    props.onChange(reorderedLinks)
  }

  const onLabelChange = (label: string, index: number) => {
    const updatedLinks = [...links]

    if (updatedLinks[index]) {
      updatedLinks[index].label = label
      props.onChange(updatedLinks)
    }
  }

  const onLinkChange = (url: string, index: number) => {
    const updatedLinks = [...links]

    if (updatedLinks[index]) {
      updatedLinks[index].url = url
      props.onChange(updatedLinks)
    }
  }

  const onDeleteLinkClick = (index: number) => {
    const updatedLinks = links.filter((_, i) => i !== index)
    props.onChange(updatedLinks)
  }

  return (
    <Container>
      <Header>
        <div style={{ width: 16 }} />
        <HeaderLabel>{t('LinksBuilder::Title')}</HeaderLabel>
        <HeaderLabel>{t('LinksBuilder::URL')}</HeaderLabel>
        <div style={{ width: 8 }} />
      </Header>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='navigation-menu-items'>
          {(provided) => {
            return (
              <Links ref={provided.innerRef} {...provided.droppableProps}>
                {links.map((link, index) => {
                  if (link.locale !== locale) return null

                  const key = `link-${index}`

                  return (
                    <Draggable key={index} draggableId={key} index={index}>
                      {(provided) => {
                        return (
                          <Link ref={provided.innerRef} {...provided.draggableProps}>
                            <LinkHandle {...provided.dragHandleProps}>
                              <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M13 9C12.17 9 11.5 9.67 11.5 10.5C11.5 11.33 12.17 12 13 12C13.83 12 14.5 11.33 14.5 10.5C14.5 9.67 13.83 9 13 9ZM3 9C2.17 9 1.5 9.67 1.5 10.5C1.5 11.33 2.17 12 3 12C3.83 12 4.5 11.33 4.5 10.5C4.5 9.67 3.83 9 3 9ZM8 9C7.17 9 6.5 9.67 6.5 10.5C6.5 11.33 7.17 12 8 12C8.83 12 9.5 11.33 9.5 10.5C9.5 9.67 8.83 9 8 9ZM13 7C13.83 7 14.5 6.33 14.5 5.5C14.5 4.67 13.83 4 13 4C12.17 4 11.5 4.67 11.5 5.5C11.5 6.33 12.17 7 13 7ZM3 4C2.17 4 1.5 4.67 1.5 5.5C1.5 6.33 2.17 7 3 7C3.83 7 4.5 6.33 4.5 5.5C4.5 4.67 3.83 4 3 4ZM8 4C7.17 4 6.5 4.67 6.5 5.5C6.5 6.33 7.17 7 8 7C8.83 7 9.5 6.33 9.5 5.5C9.5 4.67 8.83 4 8 4Z"></path></svg>
                            </LinkHandle>
                            <HeaderLabel>
                              <input
                                type='text'
                                placeholder={t('LinksBuilder::Label')}
                                onChange={(e) => onLabelChange(e.currentTarget.value, index)}
                                value={link.label}
                              />
                            </HeaderLabel>
                            <HeaderLabel>
                              <input
                                type='text'
                                placeholder={t('LinksBuilder::Link')}
                                onChange={(e) => onLinkChange(e.currentTarget.value, index)}
                                value={link.url}
                              />
                            </HeaderLabel>
                            <SimpleTooltip content={t('LinksBuilder::Delete link')}>
                            <LinkDelete onClick={() => onDeleteLinkClick(index)}>
                              <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M11.02 3L10.78 1.64999C10.61 0.689994 9.78 0 8.81 0H7.19C6.22 0 5.39 0.699994 5.22 1.64999L4.98 3H1V4.5H2.2L3.73 13.17C3.81 13.65 4.23 14 4.71 14H11.28C11.77 14 12.18 13.65 12.26 13.17L13.79 4.5H14.99V3H11.01H11.02ZM5.13 12.5L3.72 4.5H5.45L6.86 12.5H5.13ZM6.51 3L6.7 1.91C6.74 1.67 6.95 1.5 7.19 1.5H8.81C9.05 1.5 9.26 1.67 9.3 1.91L9.49 3H6.5H6.51Z"></path></svg>
                            </LinkDelete>
                            </SimpleTooltip>
                          </Link>
                        )
                      }}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </Links>
            )
          }}
        </Droppable>
      </DragDropContext>

      {activeLinks.length <= 2 && <span style={{ marginTop: 8 }}>
        <Button
          type='default'
          icon='plus'
          text={t('LinksBuilder::Add a link')}
          onClick={onAddLinkClick}
        />
      </span>}
    </Container>
  )
}

export default LinksBuilder